/**
 * The edge pages: every dead end a viewer can reach, in the business's look, as the confirmed prototype draws them
 * (g-front `#/oops`): its mark on top, one quiet mark, the words, a way on, and "Made with Flashroom". Flashroom's own
 * look when the address is nobody's. The words come from the shared copy, so the API's own pages say the same thing.
 */
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { guestLook } from "../../shared/guest-look";
import { type TroubleKind, type TroubleValues, troubleCopy, troubleIcon } from "../../shared/trouble";
import type { PublicLookResponse } from "../../shared/types";
import { BrandGlyph } from "../components/BrandMark";
import { ApiError, api } from "../lib/api";
import { Brand, Made, useGuestGround } from "./room/chrome";
import { GIcon } from "./room/GIcon";
import { keptTokens } from "./watchToken";
import "../styles/guest.css";

/** How long "door" waits before it sends the reader on by itself. */
const DOOR_MS = 1500;

/**
 * Which trouble a failed load is: the API's reason where it gave one, else the
 * connection, else the status. A 404 with no reason is a link nobody knows.
 */
export function troubleOf(err: unknown): { kind: TroubleKind; origin?: string } {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { kind: "off" };
  if (err instanceof ApiError) {
    if (err.reason === "wrong_host" && err.origin) return { kind: "door", origin: err.origin };
    if (err.reason === "unpublished") return { kind: "soon" };
    if (err.status === 0) return { kind: "off" };
    if (err.status === 404) return { kind: "seat" };
  }
  return { kind: "ours" };
}

/** The look of the workspace on this host, fetched once; nothing while it loads or when there is none. */
function useLook(given?: PublicLookResponse | null): PublicLookResponse | null {
  const [look, setLook] = useState<PublicLookResponse | null>(given ?? null);
  useEffect(() => {
    if (given) return;
    let alive = true;
    api.public
      .look()
      .then((res) => {
        if (alive) setLook(res);
      })
      .catch(() => {
        /* a page that cannot even ask for the look wears Flashroom's own */
      });
    return () => {
      alive = false;
    };
  }, [given]);
  return look;
}

/** Where "Go there now" goes: the same page, on the workspace's own address. */
export function doorTarget(origin: string, loc: { pathname: string; search: string }): string {
  return `${origin.replace(/\/+$/, "")}${loc.pathname}${loc.search}`;
}

/**
 * Their page, when this browser keeps a seat on this address. Why not the token in the address: on their own page
 * that is the one that failed.
 */
function yourPageHref(): string | null {
  const here = /^\/watch\/([^/]+)/.exec(window.location.pathname)?.[1];
  const token = keptTokens().find((t) => t !== (here ? decodeURIComponent(here) : null));
  return token ? `/watch/${encodeURIComponent(token)}` : null;
}

/** The dead ends with no button of their own: the way on is the room the business's address opens. */
const WAY_ON: ReadonlySet<TroubleKind> = new Set(["old", "seat", "down"]);
/** Where their page is a way on too: the dead ends, and a room not open yet, where their seat is kept. */
const YOURS: ReadonlySet<TroubleKind> = new Set([...WAY_ON, "soon"]);

export function TroublePage({
  kind,
  look: given,
  values,
  onAgain,
  auto = true,
}: {
  kind: TroubleKind;
  /** The look, when the page already has it; else it is asked for. */
  look?: PublicLookResponse | null;
  values?: TroubleValues;
  /** What "Try again" does; settles once the try is over. A reload when nothing is given. */
  onAgain?: () => Promise<unknown>;
  /** Whether "door" sends the reader on by itself. Off in tests. */
  auto?: boolean;
}): ReactNode {
  const look = useLook(given);
  const brand = look?.brand ?? null;
  const vars = useMemo(() => guestLook(brand ?? {}), [brand]);
  useGuestGround(vars);
  const copy = troubleCopy(kind, { host: look?.host_first_name ?? "", ...values });
  const target = kind === "door" && values?.origin ? doorTarget(values.origin, window.location) : null;
  const [trying, setTrying] = useState(false);
  const [tried, setTried] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!auto || !target) return;
    const id = window.setTimeout(() => window.location.replace(target), DOOR_MS);
    return () => window.clearTimeout(id);
  }, [auto, target]);

  const again = useCallback(async () => {
    if (!onAgain) {
      window.location.reload();
      return;
    }
    setTrying(true);
    try {
      await onAgain();
    } finally {
      // Why only while shown: a try that worked has already put the page it wanted in this one's place.
      if (alive.current) {
        setTrying(false);
        setTried(true);
      }
    }
  }, [onAgain]);

  // "We'll keep trying": offline, the connection coming back is a try of its own.
  useEffect(() => {
    if (kind !== "off") return;
    const back = () => void again();
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [kind, again]);

  const picture = look?.home ? `/api/w/${encodeURIComponent(look.home.slug)}/share.jpg` : null;
  const home = WAY_ON.has(kind) ? (look?.home ?? null) : null;
  const yours = YOURS.has(kind) ? yourPageHref() : null;
  const action = copy.action;
  const line = tried && copy.tried ? copy.tried : (copy.hint ?? "");
  return (
    <div className="gst" style={vars as CSSProperties}>
      <div className="oops">
        {picture ? <div className="oops-bg" style={{ backgroundImage: `url("${picture}")` }} /> : null}
        <div className="oops-shade" />
        <header className="top">
          {brand ? (
            <Brand brand={brand} href="/" />
          ) : (
            <a className="brand" href="https://flashsuite.ai/flashroom/" aria-label="Flashroom">
              <BrandGlyph size={34} />
              <span className="brand-w">
                <b>Flashroom</b>
              </span>
            </a>
          )}
        </header>
        <div className="oops-c" role="status">
          <span className="oops-ic">
            <GIcon name={troubleIcon(kind)} size={28} />
          </span>
          <h1>{copy.title}</h1>
          <p>{copy.body}</p>
          {action || home || yours ? (
            <div className="oops-b">
              {action?.kind === "go" && target ? (
                <a className="btn btn-p" href={target}>
                  {action.label}
                  <GIcon name="arrowr" size={18} />
                </a>
              ) : action?.kind === "back" ? (
                <a className="btn btn-g" href={values?.origin ?? "/"}>
                  {action.label}
                </a>
              ) : action ? (
                <button className="btn btn-p" type="button" disabled={trying} onClick={() => void again()}>
                  {trying ? (
                    <>
                      <span className="spin" /> Trying
                    </>
                  ) : (
                    <>
                      <GIcon name="replay" size={18} /> {action.label}
                    </>
                  )}
                </button>
              ) : null}
              {home ? (
                <a className="btn btn-p" href={`/${encodeURIComponent(home.slug)}`}>
                  Watch {home.title}
                </a>
              ) : null}
              {yours ? (
                <a className="btn btn-g" href={yours}>
                  Your page
                </a>
              ) : null}
            </div>
          ) : null}
          <p className="oops-tried" aria-live="polite">
            {line}
          </p>
        </div>
        <div className="oops-foot">
          <Made />
        </div>
      </div>
    </div>
  );
}
